import { SoftTag } from '../../Tag/SoftTag';
import { BLUE, CHROME, INK, PRIMARY } from '../../Theme/palette';
import {
  ContainerOutlined,
  FileAddOutlined,
  PlusOutlined,
  PrinterOutlined,
  RollbackOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import {
  ApprovedEstimateConfirm,
  confirmApprovedEstimate,
  useApprovedEstimate,
  InventorySelect,
  InventoryUploadDrawer,
  InventoryUploadDrawerHandle,
  ServiceDetailsCard,
  SharedEmpty,
  ServicePickerDrawer,
  ServiceSelect,
} from '@components';
import { useInventoryCategories, useRoleAccess } from '@hooks';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Badge, Button, Popover, Typography } from 'antd';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IOrderServiceSelectProps } from './OrderServiceSelect.props';
import { OrderServicePrintModal } from './OrderServicePrintModal';
import { inventoryToServicePart } from './inventoryToServicePart';
import { ReturnPartsModal } from './ReturnPartsModal';

const { Text } = Typography;

const sectionStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${CHROME.bg} 0%, ${CHROME.bgLight} 100%)`,
  borderRadius: 10,
  border: `1px solid ${CHROME.border}`,
  padding: '16px 16px 12px',
  marginBottom: 8,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: INK,
  letterSpacing: 0.2,
};

const iconWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  background: PRIMARY,
  color: '#fff',
  fontSize: 14,
};

const OrderServiceSelectPresentation: React.FC<IOrderServiceSelectProps> = ({
  atLeastOneService,
  disabled,
  variant = 'selected',
  basket,
  hideSavedOnlyActions,
  hideHeader,
  pickerOpen,
  onPickerClose,
  actionsSlot,
  headerActions,
  emptyAction,
}) => {
  const { serviceStore, orderStore } = useStore();
  const { order: access } = useRoleAccess();
  const approvedEstimate = useApprovedEstimate();
  const currentOrderId = orderStore.order?.id;
  const { options: categoryOptions } = useInventoryCategories();
  const uploadDrawerRef = useRef<InventoryUploadDrawerHandle>(null);
  const isAdditional = variant === 'additional';
  const isRevision = variant === 'revision';
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [returnPartsOpen, setReturnPartsOpen] = useState(false);
  const isMainVariant = !isAdditional && !isRevision;
  // A basket has no additional/revision sets, so the variant switching below is bypassed entirely.
  const isBasket = !!basket;

  const handleUploadSuccess = (createdItems: any[], parsed?: any[]) => {
    if (basket) {
      basket.addPartsFromInventoryBulk(createdItems, parsed);
      return;
    }
    serviceStore.addPartsFromInventoryBulk(
      createdItems,
      parsed,
      isAdditional ? 'additional' : isRevision ? 'revision' : 'selected'
    );
  };
  // Order-wide, not part of one service's row of part actions - so a host that owns the Add work
  // button can take it and put the two together.
  const printsButton = (
    <Button icon={<PrinterOutlined />} onClick={() => setPrintModalOpen(true)}>
      Print
    </Button>
  );


  const services = basket
    ? basket.selectedServices
    : isAdditional
      ? serviceStore.additionalSelectedServices
      : isRevision
        ? serviceStore.revisionSelectedServices
        : serviceStore.selectedServices;

  const partsMap = basket
    ? basket.selectedServiceParts
    : isAdditional
      ? serviceStore.additionalServiceParts
      : isRevision
        ? serviceStore.revisionServiceParts
        : serviceStore.selectedServiceParts;

  const dispatchParts = basket
    ? basket.dispatchSelectedServiceParts
    : isAdditional
      ? serviceStore.dispatchAdditionalServiceParts
      : isRevision
        ? serviceStore.dispatchRevisionServiceParts
        : serviceStore.dispatchSelectedServiceParts;

  const tenantVat = basket ? basket.tenantVat : serviceStore.tenantVat;

  const pricingSummary = basket
    ? basket.pricingSummary
    : isRevision
      ? serviceStore.revisionPricingSummary
      : serviceStore.pricingSummary;

  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

  // Keep active tab in sync: select the last service whenever the active tab is
  // missing or points to a service that no longer exists (e.g. after a revise
  // re-hydrates the list with the same count but replaced entries).
  const serviceIdsKey = services.map((s) => s.id).join(',');
  useEffect(() => {
    if (services.length === 0) {
      setActiveTab(undefined);
    } else if (!activeTab || !services.some((s) => String(s.id) === activeTab)) {
      setActiveTab(String(services[services.length - 1].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceIdsKey]);

  // For the order being edited, keep its end time anchored to the total service duration.
  // A basket has no saved order behind it, so there is nothing to anchor.
  const syncOrderEndTime = () => {
    if (isBasket || variant !== 'selected') return;
    const totalHours = serviceStore.selectedServices.reduce((acc, s) => acc + (s.duration || 0), 0);
    orderStore.syncEndFromServiceDuration(totalHours);
  };

  // With one service required, the last one cannot be removed.
  const canRemoveService = !(atLeastOneService && services.length === 1);

  const handleSelect = (id: number) => {
    if (basket) basket.addService(id);
    else if (isAdditional) serviceStore.addAdditionalService(id);
    else if (isRevision) serviceStore.addRevisionService(id);
    else serviceStore.addService(id);
    setActiveTab(String(id));
    syncOrderEndTime();
  };

  const handleDeselect = (id: number) => {
    if (basket) basket.removeService(id, atLeastOneService);
    else if (isAdditional) serviceStore.removeAdditionalService(id);
    else if (isRevision) serviceStore.removeRevisionService(id, atLeastOneService);
    else serviceStore.removeService(id, atLeastOneService);
    syncOrderEndTime();
  };

  const handleClear = () => {
    if (basket) basket.clearSelectedServices();
    else if (isAdditional) serviceStore.clearAdditionalSelectedServices();
    else if (isRevision) serviceStore.clearRevisionSelectedServices();
    else serviceStore.clearSelectedServices();
    syncOrderEndTime();
  };

  // One element, used by both returns below. Built here rather than inline in each: as two
  // separate elements React unmounted one and mounted the other the moment the first service
  // landed, which tore the open drawer down and rebuilt it - the list reloaded and the screen
  // flashed. Every service after the first stayed in the same branch, so only the first flickered.
  const picker = onPickerClose ? (
    <ServicePickerDrawer
      open={!!pickerOpen}
      mode="multiple"
      showMyServicesOnly
      title="Add work"
      selectedIds={services.map((x) => x.id)}
      selectedLabels={Object.fromEntries(services.map((x) => [x.id, Utils.formatServiceLabel(x)]))}
      onClose={onPickerClose}
      onSelect={(service) => {
        // addService resolves the id through the shared lookup, as the inline select does before
        // it raises onSelect - the picker hands over the full service instead.
        runInAction(() => {
          serviceStore.selectServices.set(service.id, service);
        });
        handleSelect(service.id);
      }}
      onDeselect={handleDeselect}
      onClear={!atLeastOneService ? handleClear : undefined}
    />
  ) : null;
  return (
    <>
      {/* Outside both bodies below. As a child of one of them, picking the first service swapped
          the body - the empty state for the list - and React tore the open drawer down with it,
          which read as the whole screen refreshing. */}
      {picker}
      {/* Into the host's own row beside Add work, when it offered one. */}
      {actionsSlot && services.length > 0 && createPortal(printsButton, actionsSlot)}
      {onPickerClose && services.length === 0 ? (
        <>
          {/* The host's Add work is what fills this state, so it has to be reachable from it. */}
          {headerActions && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
              {headerActions}
            </div>
          )}
          <SharedEmpty
            description="No work on this job yet - use Add work to pick the services"
            action={emptyAction}
          />
        </>
      ) : (
    <div style={sectionStyle}>
      {!hideHeader && (
      <div style={{ ...sectionHeaderStyle, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={iconWrapperStyle}>
            <ToolOutlined />
          </div>
          <Text style={sectionTitleStyle}>Services</Text>
          {services.length > 0 && (
            <>
              <Badge
                count={services.length}
                style={{
                  backgroundColor: PRIMARY,
                  fontSize: 11,
                  fontWeight: 600,
                  boxShadow: '0 1px 4px rgba(44, 189, 255, 0.3)',
                }}
              />
            </>
          )}
        </div>
        {!isAdditional && services.length > 0 && access.totals && (
          <div
            style={{
              display: 'flex',
              gap: 16,
              fontSize: 14,
              alignItems: 'center',
              background: '#fff',
              border: `1px solid ${CHROME.border}`,
              borderRadius: 8,
              padding: '8px 16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <span style={{ color: '#555' }}>
              Labour:{' '}
              <strong style={{ color: INK }}>
                {Utils.formatCurrency(pricingSummary.labourPrice)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              Parts:{' '}
              <strong style={{ color: INK }}>
                {Utils.formatCurrency(pricingSummary.partsPrice)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              VAT:{' '}
              <strong style={{ color: INK }}>
                {Utils.formatCurrency(pricingSummary.vatAmount)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <SoftTag
              group="blue"
              style={{ fontSize: 15, fontWeight: 700, padding: '2px 12px', margin: 0 }}
            >
              {Utils.formatCurrency(pricingSummary.totalPrice)}
            </SoftTag>
          </div>
        )}
      </div>
      )}
      {/* The inline select; when the host drives the picker there is nothing here - the drawer is
          mounted once at the top of the component. */}
      {!onPickerClose && (
      <div style={{ marginBottom: services.length > 0 ? 16 : 0 }}>
        <ServiceSelect
          mode="multiple"
          showMyServicesOnly
          placement="topLeft"
          allowClear={!atLeastOneService}
          value={services.map((x) => x.id)}
          defaultValue={services.map((x) => ({
            label: Utils.formatServiceLabel(x),
            value: x.id,
          }))}
          onClear={handleClear}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          disabled={disabled}
        />
      </div>
      )}

      {services.length > 0 && (
        <>
          {/* Tabs left, actions right - and on a narrow drawer the actions drop to a line of their
              own rather than being squeezed: they used to be overlapped by the host's own buttons. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              marginBottom: -1,
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'flex-end',
            }}
          >
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginRight: 'auto' }}>
              {services.map((s) => {
                const isActive = activeTab === String(s.id);
                const sParts = partsMap.get(s.id) || [];
                const sSubTotal = Utils.calculateLabourPrice(s);
                const sVatAmount = s.isVat
                  ? Utils.roundToTwoDecimals((sSubTotal * tenantVat) / 100)
                  : 0;
                const sPartsPrice = Utils.calculatePartsPrice(sParts);
                const sPartsVat = sParts.reduce(
                  (acc, p) =>
                    acc + Utils.calculateVat({ ...p, duration: p.quantity, vat: tenantVat }),
                  0
                );
                const tabTotal = Utils.roundToTwoDecimals(
                  sSubTotal + sVatAmount + sPartsPrice + sPartsVat
                );
                return (
                  <button
                    key={s.id}
                    title={Utils.formatServiceLabel(s)}
                    onClick={() => setActiveTab(String(s.id))}
                    style={{
                      padding: '8px 18px',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#fff' : INK,
                      background: isActive ? PRIMARY : '#fff',
                      border: isActive ? `1px solid ${PRIMARY}` : `1px solid ${CHROME.border}`,
                      borderBottom: isActive ? '1px solid #fff' : `1px solid ${CHROME.border}`,
                      borderRadius: '8px 8px 0 0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      zIndex: isActive ? 1 : 0,
                      lineHeight: 1.3,
                      textAlign: 'center',
                      width: 260,
                      maxWidth: '100%',
                    }}
                  >
                    {/* The whole name, in a tab of one fixed width: a long name wraps onto more lines
                        inside it rather than widening the tab, so the tabs stay a tidy grid. */}
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minWidth: 0 }}>
                      <ToolOutlined style={{ fontSize: 12, flexShrink: 0 }} />
                      <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                        {Utils.formatServiceLabel(s)}
                      </span>
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        marginTop: 2,
                        color: isActive ? 'rgba(255,255,255,0.85)' : BLUE.tag,
                      }}
                    >
                      {Utils.formatCurrency(tabTotal)}
                      {s.isVat && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: 9,
                            fontWeight: 600,
                            opacity: 0.75,
                          }}
                        >
                          INC VAT
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            {!disabled && activeTab && (
              /* Fill, not colour, is what separates these. Five coloured buttons in a row read as
                  a wall and nothing leads; Add extra is the action this row exists for, so it is the
                  only filled one and the rest are plain. */
              <div style={{ display: 'flex', gap: 8, paddingBottom: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* On an approved estimate job this asks first: the price was agreed. */}
                <ApprovedEstimateConfirm
                  onConfirm={() =>
                    dispatchParts({ type: 'ADD_SERVICE_PART', serviceId: Number(activeTab) })
                  }
                >
                  {(trigger) => (
                    <Button type="primary" icon={<PlusOutlined />} {...trigger}>
                      Add extra
                    </Button>
                  )}
                </ApprovedEstimateConfirm>
                <Popover
                  trigger="click"
                  placement="topRight"
                  content={
                    <div style={{ width: 650 }}>
                      <InventorySelect
                        onSelect={(item) => {
                          const add = () =>
                            dispatchParts({
                              type: 'ADD_SERVICE_PART_FROM_INVENTORY',
                              serviceId: Number(activeTab),
                              servicePart: inventoryToServicePart(item),
                            });
                          // Picked inside a popover, where a Popconfirm has nothing to anchor to.
                          if (approvedEstimate) confirmApprovedEstimate(add);
                          else add();
                        }}
                      />
                    </div>
                  }
                >
                  <Button icon={<ContainerOutlined />}>From Inventory</Button>
                </Popover>
                {access.partsActions && (
                  <Button
                    icon={<FileAddOutlined />}
                    onClick={() =>
                      uploadDrawerRef.current?.open({
                        orderId: currentOrderId,
                        serviceOptions: services.map((s) => ({
                          id: s.id,
                          name: Utils.formatServiceLabel(s),
                        })),
                      })
                    }
                  >
                    Upload parts
                  </Button>
                )}
                {/* Returns run against a saved order's extras, so they are meaningless for a job
                    that has not been created yet. */}
                {isMainVariant && access.partsActions && !isBasket && !hideSavedOnlyActions && (
                  <Button icon={<RollbackOutlined />} onClick={() => setReturnPartsOpen(true)}>
                    Return parts
                  </Button>
                )}
                {/* Only when the host has nowhere better: with a slot it sits beside Add work. */}
                {actionsSlot === undefined && printsButton}
                {/* The host's own action closes the row, so Print and Add work stay together. */}
                {headerActions}
              </div>
            )}
          </div>
          <div
            style={{
              border: `1px solid ${CHROME.border}`,
              borderRadius: '0 8px 8px 8px',
              background: '#fff',
              padding: 4,
            }}
          >
            {services
              .filter((s) => String(s.id) === activeTab)
              .map((s) => (
                <ServiceDetailsCard
                  key={s.id}
                  service={s}
                  disabled={disabled}
                  variant={variant}
                  basket={basket}
                  onRemove={() => handleDeselect(s.id)}
                  canRemove={canRemoveService}
                />
              ))}
          </div>
        </>
      )}
      <InventoryUploadDrawer
        ref={uploadDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={handleUploadSuccess}
      />
      <OrderServicePrintModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        services={services}
        partsMap={partsMap}
        tenantVat={tenantVat}
        orderTitle={orderStore.order?.title}
        registrationNumber={orderStore.order?.registrationNumber}
        customerName={orderStore.order?.customer?.fullname}
        mileage={orderStore.order?.mileage}
      />
      <ReturnPartsModal open={returnPartsOpen} onClose={() => setReturnPartsOpen(false)} />
    </div>
      )}
    </>
  );
};

export default observer(OrderServiceSelectPresentation);
